/**
 * Conteo físico (physical inventory count) API client.
 *
 * CSRF/JSON pattern mirrors anticipos.ts: read the `pacebi_csrf` cookie and
 * echo it back via X-CSRF-Token on every state-changing request. The CSV
 * import follows priceUpload.ts's multipart pattern: X-CSRF-Token is set but
 * Content-Type is NOT — the browser injects the multipart boundary itself.
 *
 * NOTE on idempotency_key: unlike anticipos.ts/payments.ts, the physcount Go
 * handlers (pacebi/internal/server/handlers_physcount.go) do NOT declare an
 * IdempotencyKey field on their request-body structs, and readJSON() there
 * decodes with json.Decoder.DisallowUnknownFields(). Sending an
 * `idempotency_key` field on createSession's or patchLine's JSON body would
 * therefore fail the whole request with 400 "bad json" instead of being
 * ignored. It's deliberately omitted here — see the Task 7 report for the
 * full read of the backend code that motivated this.
 */
import type {
  CountSession,
  CountLineView,
  CountSummary,
  CountCsvReport,
  PostCountResponse,
} from './types';

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

function csrfHeaders(): Record<string, string> {
  const csrf = readCookie('pacebi_csrf');
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (csrf) h['X-CSRF-Token'] = csrf;
  return h;
}

/**
 * Headers for requests with no JSON body: DELETE, and the multipart CSV
 * import (which must NOT set Content-Type — the browser injects the
 * multipart boundary).
 */
function csrfOnlyHeaders(): Record<string, string> {
  const csrf = readCookie('pacebi_csrf');
  const h: Record<string, string> = {};
  if (csrf) h['X-CSRF-Token'] = csrf;
  return h;
}

async function getJSON<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: 'same-origin' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

/** List all count sessions, newest first. GET /api/count-sessions. */
export function listSessions(): Promise<CountSession[]> {
  return getJSON<CountSession[]>('/api/count-sessions');
}

/** Get one session by id. GET /api/count-sessions/:id. */
export function getSession(id: number): Promise<CountSession> {
  return getJSON<CountSession>(`/api/count-sessions/${id}`);
}

/**
 * Thrown by createSession when the requested scope is wider than the
 * server's confirm threshold (409 {error:"scope_too_large", count}). The
 * caller re-submits the same body with confirm:true to proceed anyway.
 */
export class ScopeTooLargeError extends Error {
  readonly count: number;
  constructor(count: number) {
    super('scope_too_large');
    this.name = 'ScopeTooLargeError';
    this.count = count;
  }
}

export interface CreateCountSessionBody {
  sid: number;
  name: string;
  scope_type: string;
  scope_from: string;
  scope_to: string;
  class_filter: string;
  post_mode: string;
  uncounted: string;
  blind: boolean;
  /** Re-submit true after a ScopeTooLargeError to freeze the scope anyway. */
  confirm?: boolean;
}

/**
 * Create (== freeze) a new count session: snapshots the live scope from
 * paceAPIserv straight into the session's lines. POST /api/count-sessions.
 * Throws ScopeTooLargeError on 409 — re-submit with confirm:true to proceed.
 */
export async function createSession(body: CreateCountSessionBody): Promise<CountSession> {
  const res = await fetch('/api/count-sessions', {
    method: 'POST',
    credentials: 'same-origin',
    headers: csrfHeaders(),
    body: JSON.stringify(body),
  });
  if (res.status === 409) {
    const payload = await res.json().catch(() => ({ count: 0 }));
    throw new ScopeTooLargeError(typeof payload.count === 'number' ? payload.count : 0);
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
  return (await res.json()) as CountSession;
}

/** Hard-delete a session and its lines. DELETE /api/count-sessions/:id. */
export async function deleteSession(id: number): Promise<void> {
  const res = await fetch(`/api/count-sessions/${id}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: csrfOnlyHeaders(),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
}

/**
 * The variance/valuation/drift review view for a session: every line plus
 * the session-level rollup. Drift is re-read live from paceAPIserv, so this
 * can show whether on-hand has moved since the count was frozen.
 * GET /api/count-sessions/:id/lines.
 */
export function getLines(id: number): Promise<{ lines: CountLineView[]; summary: CountSummary }> {
  return getJSON<{ lines: CountLineView[]; summary: CountSummary }>(`/api/count-sessions/${id}/lines`);
}

/**
 * Record (or, passing counted:null, clear) one line's counted qty — the
 * manual-entry path. PUT /api/count-sessions/:id/lines/:lineID.
 */
export async function patchLine(id: number, lineID: number, counted: number | null): Promise<void> {
  const res = await fetch(`/api/count-sessions/${id}/lines/${lineID}`, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: csrfHeaders(),
    body: JSON.stringify({ counted }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
}

/**
 * Bulk manual entry via CSV upload ("line,part,counted"). Every row is
 * classified (applied/unmatched/duplicate/non_numeric) instead of the whole
 * upload succeeding-or-failing atomically.
 * POST multipart /api/count-sessions/:id/import.
 */
export async function importCsv(id: number, file: File): Promise<CountCsvReport> {
  const fd = new FormData();
  fd.append('file', file);

  const res = await fetch(`/api/count-sessions/${id}/import`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: csrfOnlyHeaders(),
    body: fd,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
  return (await res.json()) as CountCsvReport;
}

/**
 * Thrown by postSession when writes are disabled. The server deliberately
 * 404s rather than 403 for this route (handlers_physcount.go) so the SPA can
 * key off it to hide the "Postear" controls entirely — same 404 convention
 * dispatchPriceUpload uses (priceUpload.ts), just as a typed error here
 * instead of a bare Error(message) since createSession already established
 * the typed-error-class pattern for this module (ScopeTooLargeError above).
 */
export class WritesDisabledError extends Error {
  constructor() {
    super('writes_disabled');
    this.name = 'WritesDisabledError';
  }
}

/**
 * Post a session's postable lines to on-hand stock via paceWriteServ's
 * post-count verb (countpost.p, Task 11). Body carries only mode/uncounted
 * overrides — deliberately NO idempotency_key: unlike the AR write verbs,
 * per-line post_status IS the idempotency mechanism server-side (a line
 * already "posted" is never re-sent), and readJSON() there decodes with
 * DisallowUnknownFields, so an idempotency_key field would 400 "bad json".
 * "Postear restantes" (retrying after a partial post) is the same call —
 * only non-terminal lines are re-sent. POST /api/count-sessions/:id/post.
 */
export async function postSession(
  id: number,
  body: { mode: string; uncounted: string },
): Promise<PostCountResponse> {
  const res = await fetch(`/api/count-sessions/${id}/post`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: csrfHeaders(),
    body: JSON.stringify(body),
  });
  if (res.status === 404) throw new WritesDisabledError();
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
  return (await res.json()) as PostCountResponse;
}
